import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { StarDisplay } from "../components/StarRating";
import { LevelCard, LevelBadge, BadgeList, SkillBadgeList } from "../components/LevelBadge";
import { ShareLogo } from "../components/ShareLogo";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Progress } from "../components/ui/progress";
import NotificationSettings from "../components/NotificationSettings";
import { 
  Camera, 
  ShieldCheck,
  Pencil,
  Check,
  X,
  Gift,
  Copy,
  Share2,
  Users,
  Award,
  TrendingUp,
  Zap,
  Bell,
  Download,
  Trash2,
  Loader2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Settings,
  LogOut,
  Star,
  Shield,
  HeartHandshake,
  Eye,
  Lock,
  Home,
  UserSearch,
  ThumbsUp,
  ThumbsDown,
  Clock,
  MapPin,
  RefreshCw
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Link } from "react-router-dom";

const ProfilePage = () => {
  const { user, token, refreshUser, logout } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [goodDeedStats, setGoodDeedStats] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [editData, setEditData] = useState({
    full_name: user?.full_name || "",
    bio: user?.bio || ""
  });
  const fileInputRef = useRef(null);
  
  // Crossed profiles state
  const [showCrossedProfiles, setShowCrossedProfiles] = useState(false);
  const [crossedProfiles, setCrossedProfiles] = useState([]);
  const [crossedIndex, setCrossedIndex] = useState(0);
  const [loadingCrossed, setLoadingCrossed] = useState(false);
  const [ratingUser, setRatingUser] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingReason, setRatingReason] = useState("");
  const [ratingCategory, setRatingCategory] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  // Rating categories
  const ratingCategories = [
    { id: "comportement", label: "Comportement", icon: "😊" },
    { id: "ponctualite", label: "Ponctualité", icon: "⏰" },
    { id: "honnetete", label: "Honnêteté", icon: "🤝" },
    { id: "respect", label: "Respect", icon: "🙏" },
    { id: "aide", label: "Aide apportée", icon: "💪" },
  ];

  const fetchProgressData = useCallback(async () => {
    try {
      const [progressRes, statsRes] = await Promise.all([
        axios.get(`${API}/users/${user?.id}/progress`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/good-deeds/my-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProgress(progressRes.data);
      setGoodDeedStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [token, user?.id]);

  useEffect(() => {
    if (token && user?.id) {
      fetchProgressData();
    }
  }, [token, user?.id, fetchProgressData]);

  // Get user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
        () => console.log("Location not available")
      );
    }
  }, []);

  const fetchCrossedProfiles = async () => {
    if (!userLocation) {
      toast.error("Position GPS requise pour voir les profils croisés");
      return;
    }
    
    setLoadingCrossed(true);
    try {
      const res = await axios.get(
        `${API}/good-deeds/crossed-profiles?lat=${userLocation[0]}&lng=${userLocation[1]}&minutes=60`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCrossedProfiles(res.data.profiles);
      setCrossedIndex(0);
      setShowCrossedProfiles(true);
      if (res.data.profiles.length === 0) {
        toast.info("Aucun profil croisé dans la dernière heure");
      }
    } catch (error) {
      toast.error("Erreur lors de la recherche des profils");
    } finally {
      setLoadingCrossed(false);
    }
  };

  const nextCrossedProfile = () => {
    if (crossedIndex < crossedProfiles.length - 1) {
      setCrossedIndex(crossedIndex + 1);
      setSelectedRating(0);
      setRatingReason("");
      setRatingCategory("");
    }
  };

  const prevCrossedProfile = () => {
    if (crossedIndex > 0) {
      setCrossedIndex(crossedIndex - 1);
      setSelectedRating(0);
      setRatingReason("");
      setRatingCategory("");
    }
  };

  const submitRating = async (profileId) => {
    if (selectedRating === 0) {
      toast.error("Sélectionnez une note");
      return;
    }
    if (!ratingCategory) {
      toast.error("Sélectionnez une catégorie");
      return;
    }
    if (!ratingReason.trim()) {
      toast.error("Expliquez pourquoi vous donnez cette note");
      return;
    }
    if (ratingReason.trim().length < 10) {
      toast.error("Votre explication doit faire au moins 10 caractères");
      return;
    }
    
    setSubmittingRating(true);
    try {
      await axios.post(
        `${API}/ratings`,
        {
          rated_user_id: profileId,
          stars: selectedRating,
          category: ratingCategory,
          comment: ratingReason.trim(),
          requires_verification: true  // Envoi pour vérification interne
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        <div>
          <p className="font-bold">Note envoyée pour vérification !</p>
          <p className="text-sm">L'utilisateur sera notifié pour confirmer.</p>
        </div>
      );
      setSelectedRating(0);
      setRatingReason("");
      setRatingCategory("");
      // Move to next profile
      if (crossedIndex < crossedProfiles.length - 1) {
        nextCrossedProfile();
      } else {
        setShowCrossedProfiles(false);
        toast.success("Vous avez noté tous les profils !");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la note");
    } finally {
      setSubmittingRating(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U";
  };

  const getPhotoUrl = () => {
    if (user?.profile_photo && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.profile_photo}?auth=${token}`;
    }
    return null;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seules les images sont acceptées"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("L'image ne doit pas dépasser 5 Mo"); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/upload/profile-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      await refreshUser();
      toast.success("Photo mise à jour !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/users/profile`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: editData
      });
      await refreshUser();
      setEditing(false);
      toast.success("Profil mis à jour !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await axios.get(`${API}/user/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `share_export_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/user/delete-account`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Compte supprimé");
      logout();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      setDeletingAccount(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || "");
    toast.success("Code copié !");
  };

  // Menu sections with colors
  const menuSections = [
    {
      id: "stats",
      title: "Mes Statistiques",
      icon: TrendingUp,
      color: "#3B82F6",
      bgColor: "from-blue-500 to-blue-600"
    },
    {
      id: "referral",
      title: "Parrainage",
      subtitle: `${user?.referral_count || 0} filleuls`,
      icon: Gift,
      color: "#F59E0B",
      bgColor: "from-amber-500 to-orange-500"
    },
    {
      id: "notifications",
      title: "Notifications",
      icon: Bell,
      color: "#8B5CF6",
      bgColor: "from-violet-500 to-purple-600"
    },
    {
      id: "circles",
      title: "Cercles de Confiance",
      icon: Users,
      color: "#22C55E",
      bgColor: "from-green-500 to-emerald-500"
    },
    {
      id: "edit",
      title: "Modifier le profil",
      icon: Pencil,
      color: "#6366F1",
      bgColor: "from-indigo-500 to-indigo-600"
    }
  ];

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 pb-24">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Header Card */}
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="bg-gradient-to-br from-[#FF3366] to-[#FF6B6B] p-6 pb-20 relative">
            <div className="absolute top-4 right-4">
              <button
                onClick={logout}
                className="p-2 bg-white/20 rounded-full text-white hover:bg-white/30 transition-all"
              >
                <LogOut size={20} />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <ShareLogo size={36} />
              <span className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>RATE<span className="text-amber-400">⭐</span>SHARE</span>
            </div>
          </div>
          
          <CardContent className="pt-0 -mt-16 relative z-10">
            {/* Avatar */}
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <Avatar className="h-28 w-28 border-4 border-white shadow-xl">
                  <AvatarImage src={getPhotoUrl()} alt={user?.full_name} />
                  <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200 text-[#FF3366] text-2xl font-bold">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 p-2.5 bg-[#FF3366] text-white rounded-full shadow-lg hover:bg-[#E62E5C] transition-all active:scale-95"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </div>

              {/* Name & Level */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Outfit' }}>
                    {user?.full_name}
                  </h1>
                  {user?.is_verified && <ShieldCheck size={22} className="text-green-500" />}
                </div>
                <p className="text-slate-500 text-sm mb-3">{user?.email}</p>
                
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <LevelBadge level={user?.level} size="default" />
                  {goodDeedStats?.has_protecteur && (
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Shield size={14} /> Protecteur
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-2 mt-6">
              <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl">
                <Star size={18} className="mx-auto text-amber-500 mb-1" />
                <div className="text-lg font-bold text-amber-600">{user?.average_rating?.toFixed(1) || "0.0"}</div>
                <div className="text-[10px] text-amber-600">Note</div>
              </div>
              <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
                <Users size={18} className="mx-auto text-blue-500 mb-1" />
                <div className="text-lg font-bold text-blue-600">{user?.total_ratings || 0}</div>
                <div className="text-[10px] text-blue-600">Avis</div>
              </div>
              <div className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
                <HeartHandshake size={18} className="mx-auto text-green-500 mb-1" />
                <div className="text-lg font-bold text-green-600">{goodDeedStats?.total_points || 0}</div>
                <div className="text-[10px] text-green-600">Points</div>
              </div>
              <div className="text-center p-3 bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl">
                <Gift size={18} className="mx-auto text-pink-500 mb-1" />
                <div className="text-lg font-bold text-pink-600">{user?.referral_count || 0}</div>
                <div className="text-[10px] text-pink-600">Filleuls</div>
              </div>
            </div>

            {/* Badges */}
            {user?.badges && user.badges.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <BadgeList badges={user.badges} size="default" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Menu Grid */}
        <div className="grid grid-cols-2 gap-3">
          {menuSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              className={`p-4 rounded-2xl text-left transition-all active:scale-95 ${
                activeSection === section.id 
                  ? `bg-gradient-to-br ${section.bgColor} text-white shadow-lg` 
                  : 'bg-white shadow hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                activeSection === section.id 
                  ? 'bg-white/20' 
                  : ''
              }`} style={activeSection !== section.id ? { backgroundColor: section.color + '15' } : {}}>
                <section.icon size={20} style={{ color: activeSection === section.id ? 'white' : section.color }} />
              </div>
              <div className={`font-bold text-sm ${activeSection === section.id ? 'text-white' : 'text-slate-800'}`}>
                {section.title}
              </div>
              {section.subtitle && (
                <div className={`text-xs ${activeSection === section.id ? 'text-white/80' : 'text-slate-500'}`}>
                  {section.subtitle}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Active Section Content */}
        {activeSection === "stats" && (
          <Card className="animate-in slide-in-from-top-2 border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <TrendingUp size={20} />
                Mes Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {progress && <LevelCard user={user} progress={progress} />}
              
              {goodDeedStats && (
                <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-700">Progression Protecteur</span>
                    <span className="text-xs text-green-600">
                      {goodDeedStats.has_protecteur ? `${goodDeedStats.protecteur_days_remaining}j restants` : `${goodDeedStats.points_to_protecteur} pts manquants`}
                    </span>
                  </div>
                  <Progress value={goodDeedStats.has_protecteur ? 100 : (goodDeedStats.total_points / 100) * 100} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeSection === "referral" && (
          <Card className="animate-in slide-in-from-top-2 border-amber-200">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Gift size={20} />
                Parrainage
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl">
                <div className="flex items-center gap-2 text-green-700">
                  <Gift size={18} />
                  <span className="font-bold text-sm">+1 mois gratuit par filleul !</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex-1 p-4 bg-gradient-to-r from-[#FF3366]/10 to-pink-50 rounded-2xl border-2 border-dashed border-[#FF3366]/30">
                  <p className="text-center font-mono text-2xl font-bold tracking-widest text-[#FF3366]">
                    {user?.referral_code || "..."}
                  </p>
                </div>
                <Button onClick={copyReferralCode} className="h-14 w-14 rounded-2xl bg-[#FF3366] hover:bg-[#E62E5C]">
                  <Copy size={20} />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-slate-50 rounded-2xl text-center">
                  <Users size={20} className="mx-auto text-slate-500 mb-1" />
                  <span className="text-2xl font-bold text-[#FF3366]">{user?.referral_count || 0}</span>
                  <p className="text-xs text-slate-500">Filleuls</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-2xl text-center">
                  <Zap size={20} className="mx-auto text-amber-500 mb-1" />
                  <span className="text-2xl font-bold text-amber-600">{user?.referral_points || 0}</span>
                  <p className="text-xs text-amber-600">Points</p>
                </div>
              </div>

              <Button
                className="w-full h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                onClick={() => {
                  const shareUrl = `${window.location.origin}/auth?mode=register&ref=${user?.referral_code}`;
                  if (navigator.share) {
                    navigator.share({ title: "Rejoins RATESHARE !", url: shareUrl });
                  } else {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success("Lien copié !");
                  }
                }}
              >
                <Share2 size={18} className="mr-2" />
                Partager mon lien
              </Button>
            </CardContent>
          </Card>
        )}

        {activeSection === "notifications" && (
          <Card className="animate-in slide-in-from-top-2 border-violet-200">
            <CardHeader className="bg-gradient-to-r from-violet-50 to-purple-50">
              <CardTitle className="flex items-center gap-2 text-violet-700">
                <Bell size={20} />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Link to pending ratings */}
              <Link
                to="/notifications"
                className="w-full flex items-center justify-between p-4 bg-amber-50 hover:bg-amber-100 rounded-2xl transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Star size={20} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800">Notes en attente</p>
                    <p className="text-xs text-slate-500">Confirmez ou contestez vos notes</p>
                  </div>
                </div>
                <ChevronRight className="text-slate-400" size={20} />
              </Link>
              
              {/* Push notification settings */}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-600 mb-3">Paramètres Push</p>
                <NotificationSettings onClose={() => setActiveSection(null)} />
              </div>
            </CardContent>
          </Card>
        )}

        {activeSection === "circles" && (
          <Card className="animate-in slide-in-from-top-2 border-green-200">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Users size={20} />
                Cercles de Confiance
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <p className="text-slate-600 text-sm">
                Rejoignez ou créez des cercles pour votre quartier, école ou travail.
              </p>
              
              <Link
                to="/cercles"
                className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-2xl transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                    <Users size={20} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800">Mes Cercles</p>
                    <p className="text-xs text-slate-500">Voir et gérer mes cercles</p>
                  </div>
                </div>
                <ChevronRight className="text-slate-400" size={20} />
              </Link>
            </CardContent>
          </Card>
        )}

        {activeSection === "edit" && (
          <Card className="animate-in slide-in-from-top-2 border-indigo-200">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-indigo-100">
              <CardTitle className="flex items-center gap-2 text-indigo-700">
                <Pencil size={20} />
                Modifier le profil
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="font-bold text-slate-700">Nom complet</Label>
                <Input
                  id="full_name"
                  value={editData.full_name}
                  onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                  className="rounded-xl h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio" className="font-bold text-slate-700">Bio</Label>
                <Textarea
                  id="bio"
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  rows={3}
                  placeholder="Parlez-nous de vous..."
                  className="rounded-xl resize-none"
                />
              </div>
              <Button
                onClick={handleSaveProfile}
                className="w-full h-12 rounded-2xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700"
              >
                <Check size={18} className="mr-2" />
                Enregistrer les modifications
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Skill Badges */}
        {user?.skill_badges && user.skill_badges.length > 0 && (
          <Card className="border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Award size={20} />
                Badges de Compétences
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <SkillBadgeList badges={user.skill_badges} size="default" />
            </CardContent>
          </Card>
        )}

        {/* Logout Button */}
        <Button
          variant="outline"
          onClick={logout}
          className="w-full h-12 rounded-2xl border-2 border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          <LogOut size={18} className="mr-2" />
          Se déconnecter
        </Button>
      </div>

      {/* Crossed Profiles Modal */}
      {showCrossedProfiles && crossedProfiles.length > 0 && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FF3366] to-[#FF6B6B] p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <UserSearch size={20} />
                <span className="font-bold">Profils Croisés</span>
              </div>
              <button
                onClick={() => setShowCrossedProfiles(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Profile Card */}
            {crossedProfiles[crossedIndex] && (
              <div className="p-6">
                {/* Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={prevCrossedProfile}
                    disabled={crossedIndex === 0}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center disabled:opacity-30"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="text-sm text-slate-500">
                    {crossedIndex + 1} / {crossedProfiles.length}
                  </span>
                  <button
                    onClick={nextCrossedProfile}
                    disabled={crossedIndex >= crossedProfiles.length - 1}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center disabled:opacity-30"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Profile Info */}
                <div className="text-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center mx-auto mb-3 overflow-hidden border-4 border-white shadow-lg">
                    {crossedProfiles[crossedIndex].profile_photo ? (
                      <img src={crossedProfiles[crossedIndex].profile_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl font-bold text-[#FF3366]">
                        {getInitials(crossedProfiles[crossedIndex].full_name)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-1">
                    {crossedProfiles[crossedIndex].full_name}
                  </h3>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Clock size={14} />
                    <span>
                      Croisé il y a {Math.round((Date.now() - new Date(crossedProfiles[crossedIndex].crossed_at).getTime()) / 60000)} min
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                      {crossedProfiles[crossedIndex].level}
                    </span>
                  </div>
                </div>

                {/* Rating Stars */}
                <div className="mb-3">
                  <p className="text-center text-sm text-slate-600 mb-2">Évaluez cette interaction</p>
                  <div className="flex justify-center gap-2" data-testid="rating-stars-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        data-testid={`rating-star-${star}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedRating(star);
                        }}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all active:scale-90 cursor-pointer select-none ${
                          selectedRating >= star 
                            ? 'bg-amber-400 text-white shadow-lg' 
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        <Star size={20} fill={selectedRating >= star ? 'white' : 'none'} className="pointer-events-none" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category Selection */}
                {selectedRating > 0 && (
                  <div className="mb-3 animate-in slide-in-from-top-2">
                    <p className="text-center text-sm text-slate-600 mb-2">Catégorie</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {ratingCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setRatingCategory(cat.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            ratingCategory === cat.id
                              ? 'bg-[#FF3366] text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reason Input */}
                {selectedRating > 0 && ratingCategory && (
                  <div className="mb-4 animate-in slide-in-from-top-2">
                    <p className="text-center text-sm text-slate-600 mb-2">Pourquoi cette note ?</p>
                    <textarea
                      value={ratingReason}
                      onChange={(e) => setRatingReason(e.target.value)}
                      placeholder="Expliquez votre note (min. 10 caractères)..."
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-[#FF3366] focus:border-transparent"
                      rows={2}
                      maxLength={200}
                    />
                    <p className="text-right text-xs text-slate-400 mt-1">{ratingReason.length}/200</p>
                  </div>
                )}

                {/* Info Notice */}
                {selectedRating > 0 && ratingCategory && ratingReason.length >= 10 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100 animate-in slide-in-from-top-2">
                    <div className="flex items-start gap-2">
                      <ShieldCheck size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">
                        Votre note sera envoyée pour <strong>vérification interne</strong> et l'utilisateur recevra une notification pour <strong>confirmer</strong>.
                      </p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={nextCrossedProfile}
                    className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-600 font-medium flex items-center justify-center gap-2 hover:bg-slate-200 transition-all"
                  >
                    <X size={18} />
                    Passer
                  </button>
                  <button
                    onClick={() => submitRating(crossedProfiles[crossedIndex].id)}
                    disabled={selectedRating === 0 || !ratingCategory || ratingReason.length < 10 || submittingRating}
                    className="flex-1 h-12 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B6B] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-95"
                  >
                    {submittingRating ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <ShieldCheck size={18} />
                        Envoyer
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fixed Bottom - Accueil + Profils Croisés */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 safe-area-bottom">
        <div className="flex gap-3">
          <Link
            to="/dashboard"
            className="flex-1 flex items-center justify-center gap-2 h-14 bg-gradient-to-r from-[#FF3366] to-[#FF6B6B] text-white font-bold rounded-2xl shadow-lg shadow-pink-200 active:scale-95 transition-all"
          >
            <Home size={20} />
            <span>Accueil</span>
          </Link>
          <button
            onClick={fetchCrossedProfiles}
            disabled={loadingCrossed}
            className="flex-1 flex items-center justify-center gap-2 h-14 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-2xl shadow-lg shadow-amber-200 active:scale-95 transition-all disabled:opacity-70"
          >
            {loadingCrossed ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <UserSearch size={20} />
            )}
            <span>Noter</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
